import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { switchMap, tap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  createDeeplinkPathFromFlightSearchParams,
  mapOndMarketingOfferToFlightSearchParams,
} from '@fcom/common/utils/flight-search.utils';
import { LanguageService } from '@fcom/ui-translate';

import { OndMarketingOffersService } from '../../services/ond-marketing-offers.service';
import { OndMarketingOffer, OndPairEnhancedResponse } from '../../interfaces/ond-marketing-offers.interface';

const INITIAL_OFFER_AMOUNT = 6;
const OFFERS_TO_LOAD_AMOUNT = 3;

@Component({
  selector: 'fin-ond-marketing-offers',
  templateUrl: './ond-marketing-offers.component.html',
  styleUrls: ['./ond-marketing-offers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OndMarketingOffersComponent {
  private ondMarketingOffersService = inject(OndMarketingOffersService);
  private languageService = inject(LanguageService);
  private router = inject(Router);

  readonly SvgLibraryIcon = SvgLibraryIcon;

  originLocationCode = input<string | undefined>(undefined);
  destinationLocationCode = input<string | undefined>(undefined);
  links = input<OndPairEnhancedResponse>();

  readonly moreOffersAmount: number = OFFERS_TO_LOAD_AMOUNT;
  offerAmount = signal(INITIAL_OFFER_AMOUNT);

  readonly ondOffers = signal<OndMarketingOffer[]>([]);
  readonly heading = signal('');

  constructor() {
    effect((onCleanup) => {
      const origin = this.originLocationCode();
      const destination = this.destinationLocationCode();
      const links = this.links();

      if (!destination && !links) {
        return;
      }

      const subscription = this.ondMarketingOffersService
        .getOffersOrFlightData(origin, destination, links)
        .pipe(
          tap((offers) => this.ondOffers.set(offers)),
          switchMap((offers) =>
            this.ondMarketingOffersService.getHeading(origin, destination, offers, 'mostPopularDeals')
          )
        )
        .subscribe((heading) => this.heading.set(heading));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  loadMoreOffers(): void {
    this.offerAmount.update((amount) => amount + this.moreOffersAmount);
  }

  navigateToUrl(ondOffer: OndMarketingOffer): void {
    const flightSearchParams = mapOndMarketingOfferToFlightSearchParams(ondOffer, this.languageService.langValue);
    if (flightSearchParams) {
      const deeplink = createDeeplinkPathFromFlightSearchParams(flightSearchParams);
      this.router.navigateByUrl(deeplink);
    }
  }
}
